import {gsap} from "gsap";
import {
    ActionSpinOptions,
    ActionSpinSound,
    ASWinData,
    PromoPanelGameConfiguration
} from "@nolimitcity/promo-panel/bin/interfaces/ASInterfaces";
import {BonusBuyPopUpContent} from "@nolimitcity/promo-panel/bin/feature/generic/BonusBuyPopUpContent";
import {ASBonusPickOptions, WINTYPE} from "@nolimitcity/promo-panel/bin/enums/ASEnums";
import {HOTBGameData} from "./server/HOTBData";
import {HOTB} from "./HOTB";
import {ExtendedGameMode} from "./server/ExtendedGameMode";
import {GameUtils} from "./utils/GameUtils";
import {MathHelper} from "@nolimitcity/slot-game/bin/utils/MathHelper";
import {GameSounds} from "./sound/GameSounds";

export class GamePromoPanelConfig implements PromoPanelGameConfiguration {
    private triggeredFs: boolean;
    private featureName: string;

    public getActionSpinOptions(): ActionSpinOptions {
        return {
            gameName: "Home Of The Brave",
            hasActionSpin: true,
            graphics: {backgroundTextureName: "introBg"},

            automaticPickGame: {
                header: (): string => "",
                optionSelection: (response: any): number => 1,
                type: ASBonusPickOptions.PICK_MODE,
                pickNeededForModes: [ExtendedGameMode.CAPWIN_GAME],
                pickNeededForBuyFeatures: [ExtendedGameMode.CAPWIN_GAME] //Needs to be the actual feature name used by server.
            },

            getParsedData: (response: HOTBGameData, bet: number): ASWinData => {
                if (response.mode === ExtendedGameMode.NORMAL) {
                    this.triggeredFs = false;
                }

                const parsedData: ASWinData = {
                    totalWin: response.accumulatedRoundWin,
                    wasFeatureBuy: response.wasFeatureBuy,
                    freeSpinTriggeredThisSpin: this.isFreeSpinTriggered(response),//GameUtils.isFreeSpinTriggered(response.mode, response.nextMode),
                    totalSpinWinnings: this.getSpinWin(response),
                    isBigWin: this.isBigWin(response, bet),
                    winType: WINTYPE.NORMAL,
                    featureName: this.getFeatureName(response),
                    isRoundComplete: this.isRoundComplete(response),
                    isBonusEnd: this.isBonusEnd(response),
                    isWinCapHit: response.brokeTheBank,
                }
                return parsedData;
            }
        };
    }

    public replaceBonusBuyPopUpContent(orgContent: BonusBuyPopUpContent): PIXI.Container {
        // You can modify the orgContent to your liking, like in the simple text replace below. All params is public in orgContent, it's basically a PIXI container with some stuff in.
        // Or you can make you your own container with everything custom. You don't need to return a BonusBuyPopUpContent, it's enough with a container.
        // Use the priceString if you change the price text, it's already formatted correctly.

        return orgContent;
    }

    public onActionSpinPlaySound(trigger: ActionSpinSound): void {
        if (trigger === ActionSpinSound.WIN) {
            HOTB.soundPlayer.playSound(GameSounds.AS_WIN, 1);
        } else if (trigger === ActionSpinSound.BIG_WIN) {
            HOTB.soundPlayer.playSound(GameSounds.AS_WIN_BIG, 1);
        }
    }

    public onPlayBonusInGame(data: HOTBGameData): void {
        HOTB.apiPlugin.slotStates.resetGameStartedAndGameServerFlag();
        HOTB.model.onGame(data);
        this.handleBonusGame(data);
    }

    private handleBonusGame(data: HOTBGameData): void {
        HOTB.model.isActionSpinToBonus = true;

        HOTB.keypadPlugin.setWin(data.accumulatedRoundWin, true, GameUtils.isWinBelowStake());

        HOTB.reelArea.reInit();
        HOTB.topReel.reInit();
        HOTB.leftReel.reInit();
        HOTB.rightReel.reInit();
        HOTB.waysCounter.updateFinalWaysCounter();
        HOTB.gameMultiplier.onGameLoad();

        const fsIntroTl: gsap.core.Timeline = gsap.timeline();
        fsIntroTl.to(this, {duration: 0.5});
        fsIntroTl.add(() => {
            HOTB.fsIntro.showIntro(() => {
                HOTB.gameFlowController.start();
            });
        });
    }

    private isBigWin(response: HOTBGameData, bet: number): boolean {
        const win: number = this.getSpinWin(response);

        if (response.wasFeatureBuy || response.featureBuyName === "SATAN_SPINS") {
            const winRatio: number = MathHelper.ceilToDecimals((win / GameUtils.getBuyFeaturePrice()), 2);

            return (winRatio >= 1 && !GameUtils.isSingleWinBelowStake());
        }

        if(response.featureBuyName.includes("BOOSTED_BET")) {
            const totalCost: number = GameUtils.getBuyFeaturePrice();
            if(totalCost <= 15*response.playedBetValue){
                const winRatio: number = MathHelper.ceilToDecimals((win / response.playedBetValue), 2);

                return (winRatio >= 15 && !GameUtils.isSingleWinBelowStake());
            }
            else{
                const winRatio: number = MathHelper.ceilToDecimals((win / GameUtils.getBuyFeaturePrice()), 2);

                return (winRatio >= 1 && !GameUtils.isSingleWinBelowStake());
            }
        }

        return (MathHelper.ceilToDecimals((win / bet), 2) >= 15);
    }

    private getSpinWin(response: HOTBGameData): number {
        return response.spinRoundWin;
    }

    private isFreeSpinTriggered(response: HOTBGameData): boolean {
        if ((response.mode === ExtendedGameMode.NORMAL || response.mode === ExtendedGameMode.NORMAL_AVALANCHE || response.mode === ExtendedGameMode.FREESPIN_AVALANCHE || response.mode === ExtendedGameMode.FREESPIN_SUPER_AVALANCHE)
            && (response.nextMode === ExtendedGameMode.FREESPIN || response.nextMode === ExtendedGameMode.FREESPIN_SUPER) && !this.triggeredFs) {
            this.triggeredFs = true;
            return true;
        }

        return false;
    }

    private getFeatureName(response: HOTBGameData): string {
        if (response.nextMode === ExtendedGameMode.FREESPIN || response.mode === ExtendedGameMode.FREESPIN) {
            this.featureName = "COVFEFE SPINS";
        } else if (response.nextMode === ExtendedGameMode.FREESPIN_SUPER || response.mode === ExtendedGameMode.FREESPIN_SUPER) {
            this.featureName = "DOG EATING SPINS";
        } else if (response.mode.indexOf(ExtendedGameMode.FREESPIN) === -1) {
            this.featureName = "";
        }

        return this.featureName;
    }

    private isRoundComplete(response: HOTBGameData): boolean {
        let flag: boolean = false;

        if (response.nextMode === ExtendedGameMode.NORMAL) {
            flag = true;
        }

        if (response.nextMode === ExtendedGameMode.FREESPIN) {
            flag = true;
        }

        if (response.nextMode === ExtendedGameMode.FREESPIN_SUPER) {
            flag = true;
        }

        return flag;
    }

    private isBonusEnd(response: HOTBGameData): boolean {
        let flag: boolean = false;

        if (response.nextMode === ExtendedGameMode.NORMAL) {
            if (response.mode.includes(ExtendedGameMode.FREESPIN)) {
                flag = true;
            }

            if (response.mode === ExtendedGameMode.CAPWIN_GAME) {
                flag = true;
            }
        }

        return flag;
    }
}